import config from "@Config/index";
import { getCartLineKey, type CartItem } from "../data/cartItem";
import type { CmsOption, CmsProduct } from "./unikitCms";
import { fetchUniKitProducts, fetchUniKitProductBySlug } from "./unikitCms";

export type CartValidationResult =
  | { ok: true }
  | {
      ok: false;
      errors: string[];
      /** Cart line keys ({@link getCartLineKey}) where unit price differed from CMS (Refresh price applies). */
      stalePriceItemNames: string[];
    };

/** Max £ difference allowed between cart line and CMS-computed price (rounding). */
const PRICE_EPSILON = 0.02;

function expectedUnitPrice(
  product: CmsProduct,
  selections: Record<string, string> | undefined,
): number {
  let total = Number(product.basePrice || 0);
  if (!selections || !product.options?.length) return Math.round(total * 100) / 100;
  product.options.forEach((opt) => {
    const selected = selections[opt.name];
    if (!selected) return;
    const match = opt.values?.find((v) => v.label === selected);
    total += match?.priceModifier || 0;
  });
  return Math.round(total * 100) / 100;
}

export function getCmsUnitPriceForCartItem(product: CmsProduct, item: CartItem): number {
  return expectedUnitPrice(product, item.options);
}

/** CMS option groups that have at least one choice. */
function cmsOptionsWithValues(product: CmsProduct): CmsOption[] {
  return (product.options || []).filter((o) => (o.values?.length ?? 0) > 0);
}

/**
 * Ensures cart keys/values match the live CMS product option model (names + value labels).
 * Returns an error message or null when valid.
 */
export function validateCartItemOptions(product: CmsProduct, item: CartItem): string | null {
  const cmsOpts = cmsOptionsWithValues(product);
  const cartOpts = item.options ?? {};
  const allowedNames = new Set(cmsOpts.map((o) => o.name));

  const unknownKey = Object.keys(cartOpts).find((key) => !allowedNames.has(key));
  if (unknownKey) {
    return `"${item.name}": option "${unknownKey}" does not match this product anymore. Remove the line and add it again from the shop.`;
  }

  let error: string | null = null;
  cmsOpts.some((opt) => {
    const selected = cartOpts[opt.name];
    if (selected == null || String(selected).trim() === "") {
      error = `"${item.name}": option "${opt.name}" is missing. Remove the line and add the product again.`;
      return true;
    }
    const labels = (opt.values || []).map((v) => v.label);
    if (!labels.includes(selected)) {
      error = `"${item.name}": "${opt.name}" choice is no longer available. Remove the line or pick current options on the product page.`;
      return true;
    }
    return false;
  });

  return error;
}

/** Fetches the CMS product for a single cart line (same rules as bulk validation). */
export async function resolveCmsProductForCartItem(item: CartItem): Promise<CmsProduct | null> {
  if (item.slug) {
    try {
      return await fetchUniKitProductBySlug(item.slug);
    } catch {
      return null;
    }
  }
  if (item.categorySlug) {
    try {
      const list = await fetchUniKitProducts({ categorySlug: item.categorySlug });
      if (item.id != null) {
        const byId = list.find((p) => p.id === item.id);
        if (byId) return byId;
      }
      return list.find((p) => p.name === item.name) ?? null;
    } catch {
      return null;
    }
  }
  return null;
}

export function getCmsCategorySlug(product: CmsProduct): string | null {
  const c = product.category;
  if (
    c &&
    typeof c === "object" &&
    c !== null &&
    "slug" in c &&
    typeof (c as { slug?: string }).slug === "string"
  ) {
    return (c as { slug: string }).slug;
  }
  return null;
}

function categorySlugOf(product: CmsProduct): string | null {
  return getCmsCategorySlug(product);
}

/**
 * Validates the cart against the UniKit CMS property API (`/api/unikitout-products`).
 * Resolves each line by `slug` (preferred) or `id` / `name` within `categorySlug`, then checks
 * availability, that option keys/values match the CMS (names + labels), and that the unit price
 * matches `basePrice` + option modifiers.
 */
export async function validateUnikitCart(items: CartItem[]): Promise<CartValidationResult> {
  if (config.UNIKIT_SKIP_CART_VALIDATION) {
    return { ok: true };
  }

  if (!items.length) {
    return { ok: false, errors: ["Your cart is empty."], stalePriceItemNames: [] };
  }

  const errors: string[] = [];
  const stalePriceItemNames: string[] = [];

  const slugSet = new Set<string>();
  const categorySlugsNeedingList = new Set<string>();

  items.forEach((item) => {
    if (item.slug) {
      slugSet.add(item.slug);
    } else if (item.categorySlug) {
      categorySlugsNeedingList.add(item.categorySlug);
    }
  });

  const productBySlug = new Map<string, CmsProduct | null>();
  await Promise.all(
    [...slugSet].map(async (slug) => {
      try {
        const p = await fetchUniKitProductBySlug(slug);
        productBySlug.set(slug, p);
      } catch {
        productBySlug.set(slug, null);
      }
    }),
  );

  const productsByCategory = new Map<string, CmsProduct[]>();
  await Promise.all(
    [...categorySlugsNeedingList].map(async (catSlug) => {
      try {
        const list = await fetchUniKitProducts({ categorySlug: catSlug });
        productsByCategory.set(catSlug, list);
      } catch {
        productsByCategory.set(catSlug, []);
      }
    }),
  );

  const resolveProduct = (item: CartItem): CmsProduct | null => {
    if (item.slug) {
      return productBySlug.get(item.slug) ?? null;
    }
    if (item.categorySlug) {
      const list = productsByCategory.get(item.categorySlug) || [];
      if (item.id != null) {
        const byId = list.find((p) => p.id === item.id);
        if (byId) return byId;
      }
      return list.find((p) => p.name === item.name) ?? null;
    }
    return null;
  };

  items.forEach((item) => {
    const product = resolveProduct(item);

    if (!product) {
      errors.push(
        `"${item.name}" was not found in the catalogue (it may have been removed). Remove it from your cart and add it again from the shop.`,
      );
      return;
    }

    if (product.isAvailable === false) {
      errors.push(`"${product.name}" is no longer available. Remove it from your cart.`);
      return;
    }

    if (item.categorySlug) {
      const cmsCat = categorySlugOf(product);
      if (cmsCat && cmsCat !== item.categorySlug) {
        errors.push(
          `"${item.name}" does not match the expected category. Remove it and add it again from the correct listing.`,
        );
      }
    }

    const optionError = validateCartItemOptions(product, item);
    if (optionError) {
      errors.push(optionError);
      return;
    }

    const expected = getCmsUnitPriceForCartItem(product, item);
    if (Math.abs(expected - item.price) > PRICE_EPSILON) {
      stalePriceItemNames.push(getCartLineKey(item));
      errors.push(
        `"${item.name}": price is out of date (your cart shows £${item.price.toFixed(
          2,
        )}, current price is £${expected.toFixed(
          2,
        )}). Use "Refresh price" on that line or update the cart.`,
      );
    }
  });

  if (errors.length) {
    return { ok: false, errors, stalePriceItemNames };
  }
  return { ok: true };
}
